import React, { useMemo } from 'react';
import { useLeadIntel } from '../context/LeadIntelContext';
import { useCRM } from '../context/CRMContext';
import SafeIcon from '../common/SafeIcon';
import SafeChart from '../components/common/SafeChart';
import { formatCurrency, formatDate } from '../utils/helpers';
import { useNavigate } from 'react-router-dom';

const ComplianceDashboard = () => {
  const { signals, scores } = useLeadIntel();
  const { deals, companies } = useCRM();
  const navigate = useNavigate();

  // --- METRICS CALCULATION ---
  const metrics = useMemo(() => {
    // Definisjoner fra JSON
    const complianceSignalTypes = ["RegulatoryNeed", "ComplianceInterest", "DecisionInterest"];
    const clickSignalTypes = ["ComplianceInterest", "DecisionInterest"];
    
    // 1. Antall compliance-signaler
    const complianceSignals = signals.filter(s => complianceSignalTypes.includes(s.signal_type));
    
    // 2. Klikk på microsites
    const clickSignals = signals.filter(s => clickSignalTypes.includes(s.signal_type));
    
    // 3. Bookede møter (Stage 4 = Møte/Demo)
    const meetings = deals.filter(d => d.stage_id === 'stage-4');
    
    // 4. Vunnet
    const won = deals.filter(d => d.status === 'Vunnet');

    return {
      totalSignals: complianceSignals.length,
      clicks: clickSignals.length,
      meetings: meetings.length,
      won: won.length,
      funnelData: [
        { value: complianceSignals.length, name: 'Signal (Behov)' },
        { value: clickSignals.length, name: 'Klikk (Interesse)' },
        { value: meetings.length, name: 'Møte (Dialog)' },
        { value: won.length, name: 'Vunnet (Kunde)' }
      ]
    };
  }, [signals, deals]);

  // --- HOT LEADS (Compliance) ---
  const hotLeads = useMemo(() => {
    return scores
      .filter(s => s.band === 'Hot')
      .map(score => {
        const company = companies.find(c => c.company_id === score.company_id);
        return { ...score, company };
      })
      .filter(item => item.company);
  }, [scores, companies]);

  // --- CHART OPTIONS ---
  const funnelOption = {
    title: { text: 'Compliance Konvertering', left: 'center' },
    tooltip: { trigger: 'item', formatter: '{b} : {c}' },
    toolbox: {
      feature: {
        saveAsImage: {}
      }
    },
    series: [
      {
        name: 'Funnel',
        type: 'funnel',
        left: '10%',
        top: 60,
        bottom: 60,
        width: '80%',
        min: 0,
        max: metrics.totalSignals || 10,
        minSize: '0%',
        maxSize: '100%',
        sort: 'descending',
        gap: 2,
        label: {
          show: true,
          position: 'inside',
          formatter: '{b}: {c}'
        },
        itemStyle: {
          borderColor: '#fff',
          borderWidth: 1
        },
        data: metrics.funnelData
      }
    ]
  };

  return (
    <div className="p-8 max-w-[1600px] mx-auto min-h-screen space-y-8 animate-in fade-in slide-in-from-bottom-4">
      {/* Header */}
      <div className="flex justify-between items-center border-b border-slate-200 pb-6">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-[10px] font-black uppercase tracking-widest mb-2 border border-emerald-100">
            <SafeIcon icon="🛡️" className="w-3 h-3" /> Compliance Dashboard
          </div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Compliance Funnel</h1>
          <p className="text-slate-500 font-medium mt-1">Oversikt over signaler, interesse og konvertering innen compliance.</p>
        </div>
        <button onClick={() => window.print()} className="p-3 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 text-slate-500">
          <SafeIcon icon="🖨️" className="w-5 h-5" />
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm flex items-center gap-6">
          <div className="p-4 bg-blue-50 text-blue-600 rounded-2xl">
            <SafeIcon icon="📡" className="w-8 h-8" />
          </div>
          <div>
            <p className="text-xs font-black text-slate-400 uppercase tracking-widest">Signaler</p>
            <p className="text-3xl font-black text-slate-900">{metrics.totalSignals}</p>
            <p className="text-xs text-slate-500 mt-1">Regulatory & Interest</p>
          </div>
        </div>
        <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm flex items-center gap-6">
          <div className="p-4 bg-indigo-50 text-indigo-600 rounded-2xl">
            <SafeIcon icon="🖱️" className="w-8 h-8" />
          </div>
          <div>
            <p className="text-xs font-black text-slate-400 uppercase tracking-widest">Microsite Klikk</p>
            <p className="text-3xl font-black text-slate-900">{metrics.clicks}</p>
            <p className="text-xs text-slate-500 mt-1">Høy intensjon</p>
          </div>
        </div>
        <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm flex items-center gap-6">
          <div className="p-4 bg-emerald-50 text-emerald-600 rounded-2xl">
            <SafeIcon icon="🤝" className="w-8 h-8" />
          </div>
          <div>
            <p className="text-xs font-black text-slate-400 uppercase tracking-widest">Bookede Møter</p>
            <p className="text-3xl font-black text-slate-900">{metrics.meetings}</p>
            <p className="text-xs text-slate-500 mt-1">Konvertert</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Funnel Chart */}
        <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm min-h-[400px]">
          <h3 className="text-lg font-black text-slate-900 mb-6">Pipeline Trakt</h3>
          <SafeChart option={funnelOption} height="350px" />
        </div>

        {/* Hot Leads Table */}
        <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm flex flex-col">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-black text-slate-900 flex items-center gap-2">
              <SafeIcon icon="🔥" className="text-rose-500" /> Hot Leads – Compliance
            </h3>
            <span className="bg-rose-50 text-rose-700 px-3 py-1 rounded-full text-xs font-bold">{hotLeads.length}</span>
          </div>
          
          <div className="flex-1 overflow-y-auto">
            {hotLeads.length > 0 ? (
              <div className="space-y-3">
                {hotLeads.map((lead) => (
                  <div 
                    key={lead.company_id} 
                    onClick={() => navigate(`/companies/${lead.company_id}`)}
                    className="flex items-center justify-between p-4 rounded-xl border border-slate-100 hover:border-rose-200 hover:bg-rose-50/30 cursor-pointer transition-all group"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-slate-900 text-white rounded-xl flex items-center justify-center font-bold text-sm">
                        {lead.company?.name.substring(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-900 group-hover:text-rose-700 transition-colors">{lead.company?.name}</h4>
                        <p className="text-xs text-slate-500">{formatDate(lead.last_updated_at)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="block text-xl font-black text-rose-600">{lead.score}</span>
                      <span className="text-[9px] font-bold text-slate-400 uppercase">Score</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-slate-400">
                <SafeIcon icon="❄️" className="w-12 h-12 mb-2 opacity-50" />
                <p className="text-sm font-medium">Ingen hot leads enda.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplianceDashboard;